import { Button, Card, Empty, Flex, Tag, Tooltip, Typography } from 'antd'
import { Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { organizationsAccessListInfiniteOptions } from 'src/api/main'
import type {
  OrganizationsAccessListData,
  PaginatedOrganizationUsersList
} from 'src/api/main'
import { InfiniteSelect, UserCell } from 'src/components'
import type { OptionTransformType } from 'src/utils'
import { composeDisplayName } from 'src/utils/string'

import type { WorkspaceUser } from '../types'

const { Text } = Typography

export type GroupMemberValue = {
  id: number
  firstName: string
  lastName: string
  email: string
  photoUrl: string | null
}

type MemberSource = {
  id: number
  first_name: string
  last_name: string
  email: string
  photo?: { file: string } | null
}

export const toGroupMember = (user: MemberSource): GroupMemberValue => ({
  id: user.id,
  firstName: user.first_name,
  lastName: user.last_name,
  email: user.email,
  photoUrl: user.photo?.file ?? null
})

type Props = {
  workspaceId: number
  value?: GroupMemberValue[]
  onChange?: (next: GroupMemberValue[]) => void
}

export const UserGroupMembersField = ({
  workspaceId,
  value,
  onChange
}: Props) => {
  const { t } = useTranslation()
  const [picking, setPicking] = useState(false)

  const members = value ?? []
  const selectedIds = useMemo(
    () => new Set(members.map((m) => m.id)),
    [members]
  )

  const add = (_value: unknown, option: unknown) => {
    const member = (option as { member?: GroupMemberValue } | undefined)?.member
    if (!member || selectedIds.has(member.id)) return
    onChange?.([...members, member])
    setPicking(false)
  }

  const remove = (id: number) => {
    onChange?.(members.filter((m) => m.id !== id))
  }

  return (
    <Card
      size="small"
      title={
        <Flex align="center" gap={6}>
          <Text strong style={{ fontSize: 13 }}>
            {t('users.groups.form.members')}
          </Text>
          <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>
            {members.length}
          </Tag>
        </Flex>
      }
      extra={
        <Button
          size="small"
          type="text"
          icon={<Plus size={14} />}
          onClick={() => setPicking((prev) => !prev)}
        >
          {t('users.groups.form.addMember')}
        </Button>
      }
    >
      {picking ? (
        <InfiniteSelect<
          OrganizationsAccessListData,
          PaginatedOrganizationUsersList,
          WorkspaceUser
        >
          autoFocus
          defaultOpen
          value={null}
          onChange={add}
          style={{ width: '100%', marginBottom: 10 }}
          getQueryFunction={organizationsAccessListInfiniteOptions}
          baseOptions={{ query: { organization: workspaceId } }}
          getOptions={({ options }: OptionTransformType<WorkspaceUser>) =>
            options
              .filter((u) => !selectedIds.has(u.user.id))
              .map((u) => ({
                value: u.user.id,
                label: `${composeDisplayName(
                  u.user.first_name,
                  u.user.last_name,
                  u.user.email
                )} (${u.user.email})`,
                member: toGroupMember(u.user)
              }))
          }
          placeholder={t('users.groups.form.membersPlaceholder')}
        />
      ) : null}

      {members.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '8px 0' }}
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('users.groups.form.membersEmpty')}
            </Text>
          }
        />
      ) : (
        <Flex vertical gap={2}>
          {members.map((m) => (
            <Flex
              key={m.id}
              align="center"
              justify="space-between"
              gap={10}
              style={{
                padding: '7px 6px',
                borderBottom: '1px solid var(--ant-color-border-secondary)'
              }}
            >
              <UserCell
                firstName={m.firstName}
                lastName={m.lastName}
                email={m.email}
                photoUrl={m.photoUrl}
                size="md"
              />
              <Tooltip title={t('users.groups.form.removeMember')}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<Trash2 size={13} />}
                  onClick={() => remove(m.id)}
                  aria-label={t('users.groups.form.removeMember')}
                />
              </Tooltip>
            </Flex>
          ))}
        </Flex>
      )}
    </Card>
  )
}
